/**
 * RentalMarketIntelligence , Multifamily-only RentCast enrichment panel.
 *
 * Position: directly below LocationIntelligence on Opportunity Detail.
 * Hidden entirely for non-multifamily opportunities.
 *
 * Behavior:
 *   - On mount: if asset_type=="multifamily" and the rent estimate is missing
 *     or > 7 days old, POST to /opportunities/{oid}/rentcast/refresh once.
 *   - Otherwise: render the cached fields from the opportunity record.
 *   - Manual Refresh button bypasses the cache via ?force=true.
 *   - Shows skeleton cards while busy. On full-failure, shows a single warning
 *     row + toast , never a broken UI state.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Building2, TrendingUp, TrendingDown, AlertCircle, RefreshCw, X,
  Eye, Home, Calendar, MapPinned,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtCurrencyFull } from "@/lib/format";

const STALE_DAYS = 7;

const isFresh = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24) < STALE_DAYS;
};

const fmtMoney = (v) => (typeof v === "number" ? `$${v.toLocaleString("en-US", { maximumFractionDigits: 0 })}` : "N/A");
const fmtPct = (v) => (typeof v === "number" ? `${v.toFixed(1)}%` : "N/A");

export default function RentalMarketIntelligence({ opp, onOppUpdate }) {
  const [busy, setBusy] = useState(false);
  const [allFailed, setAllFailed] = useState(false);
  const [compsModal, setCompsModal] = useState(null); // null | "rental" | "sale"
  const triedRef = useRef(false);

  const stale = !isFresh(opp?.rc_rent_estimate_date);

  // Auto-refresh on mount when stale (only once per mount).
  useEffect(() => {
    if (!opp || opp.asset_type !== "multifamily") return;
    if (triedRef.current) return;
    if (!stale) return;
    triedRef.current = true;
    refresh(false);
  }, [opp?.oid]);

  const refresh = async (force) => {
    setBusy(true);
    setAllFailed(false);
    try {
      const r = await api.post(
        `/opportunities/${opp.oid}/rentcast/refresh`,
        null,
        { params: force ? { force: true } : {} },
      );
      if (r.data?.opp) onOppUpdate?.(r.data.opp);
    } catch (e) {
      setAllFailed(true);
      toast.message("RentCast data unavailable. Showing cached data.");
    } finally {
      setBusy(false);
    }
  };

  if (opp?.asset_type !== "multifamily") return null;

  return (
    <section className="ae-card p-5 space-y-4" data-testid="rental-market-intelligence">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Building2 className="h-3.5 w-3.5 text-emerald-400" />
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Rental Market Intelligence</div>
          {opp.rc_rent_estimate_date && (
            <span className="text-[10px] text-slate-500 ml-2" data-testid="rmi-last-updated">
              Last updated: {opp.rc_rent_estimate_date}
            </span>
          )}
          {stale && opp.rc_rent_estimate_date && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="rmi-stale-badge">
              Data may be stale
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={() => refresh(true)}
          disabled={busy}
          data-testid="rmi-refresh"
          className="btn-ghost text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {allFailed && !opp.rc_rent_estimate && (
        <div className="text-xs text-amber-300 inline-flex items-center gap-2" data-testid="rmi-empty">
          <AlertCircle className="h-3.5 w-3.5" /> RentCast data unavailable for this address. Try Refresh, or check the data pull logs.
        </div>
      )}

      {/* Row 1: 4 metric cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {busy && !opp.rc_rent_estimate ? (
          <>{[0,1,2,3].map((i) => <SkeletonCard key={i} />)}</>
        ) : (
          <>
            <MetricCard
              testid="rmi-rent-estimate"
              label="Rent Estimate"
              primary={opp.rc_rent_estimate ? `${fmtMoney(opp.rc_rent_estimate)}/mo` : "N/A"}
              secondary={
                opp.rc_rent_estimate_low && opp.rc_rent_estimate_high
                  ? `${fmtMoney(opp.rc_rent_estimate_low)} , ${fmtMoney(opp.rc_rent_estimate_high)}`
                  : null
              }
            />
            <MetricCard
              testid="rmi-market-rent"
              label="Market Avg Rent"
              primary={opp.rc_market_avg_rent ? `${fmtMoney(opp.rc_market_avg_rent)}/mo` : "N/A"}
            />
            <MetricCard
              testid="rmi-rent-growth"
              label="Rent Growth YoY"
              primary={fmtPct(opp.rc_market_rent_growth_yoy)}
              icon={
                typeof opp.rc_market_rent_growth_yoy === "number" && opp.rc_market_rent_growth_yoy >= 0
                  ? <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                  : typeof opp.rc_market_rent_growth_yoy === "number"
                    ? <TrendingDown className="h-3.5 w-3.5 text-rose-400" />
                    : null
              }
              accent={
                typeof opp.rc_market_rent_growth_yoy === "number"
                  ? (opp.rc_market_rent_growth_yoy >= 0 ? "emerald" : "rose")
                  : null
              }
            />
            <MetricCard
              testid="rmi-vacancy"
              label="Market Vacancy"
              primary={fmtPct(opp.rc_market_vacancy_rate)}
              accent={
                typeof opp.rc_market_vacancy_rate === "number"
                  ? (opp.rc_market_vacancy_rate > 8 ? "rose" : opp.rc_market_vacancy_rate < 4 ? "emerald" : null)
                  : null
              }
            />
          </>
        )}
      </div>

      {/* Row 2: Rental Comps + Sale Comps side by side */}
      <div className="grid md:grid-cols-2 gap-3">
        {busy && !opp.rc_comp_count ? (
          <><SkeletonCard tall /><SkeletonCard tall /></>
        ) : (
          <>
            <CompsPanel
              testid="rmi-rental-comps"
              title="Rental Comps"
              count={opp.rc_comp_count}
              lines={[
                `Avg rent: ${opp.rc_comp_avg_rent ? `${fmtMoney(opp.rc_comp_avg_rent)}/mo` : "N/A"}`,
                `Avg size: ${opp.rc_comp_avg_sqft ? `${Math.round(opp.rc_comp_avg_sqft).toLocaleString()} sqft` : "N/A"}`,
              ]}
              lastPulled={opp.rc_comps_last_pulled}
              radius={opp.rc_comp_radius_miles}
              hasRaw={Array.isArray(opp.rc_comps_raw) && opp.rc_comps_raw.length > 0}
              onView={() => setCompsModal("rental")}
            />
            <CompsPanel
              testid="rmi-sale-comps"
              title="Sale Comps"
              count={opp.rc_sale_comp_count}
              lines={[
                `Avg sale price: ${opp.rc_sale_comp_avg_price ? fmtCurrencyFull(opp.rc_sale_comp_avg_price) : "N/A"}`,
                `Avg $/sqft: ${opp.rc_sale_comp_avg_price_sqft ? `$${Math.round(opp.rc_sale_comp_avg_price_sqft)}` : "N/A"}`,
              ]}
              lastPulled={opp.rc_sale_comps_last_pulled}
              radius={0.5}
              hasRaw={Array.isArray(opp.rc_sale_comps_raw) && opp.rc_sale_comps_raw.length > 0}
              onView={() => setCompsModal("sale")}
            />
          </>
        )}
      </div>

      {/* Owner record strip */}
      <OwnerStrip opp={opp} />

      {compsModal && (
        <CompsModal
          title={compsModal === "rental" ? "Rental Comps" : "Sale Comps"}
          items={compsModal === "rental" ? opp.rc_comps_raw : opp.rc_sale_comps_raw}
          kind={compsModal}
          onClose={() => setCompsModal(null)}
        />
      )}
    </section>
  );
}

function MetricCard({ label, primary, secondary, icon, accent, testid }) {
  const color = accent === "emerald" ? "text-emerald-300" : accent === "rose" ? "text-rose-300" : "text-slate-100";
  return (
    <div className="ae-card p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`font-mono text-lg tabular-nums mt-1 inline-flex items-center gap-1.5 ${color}`}>
        {icon}{primary}
      </div>
      {secondary && <div className="text-[10px] text-slate-500 mt-0.5">{secondary}</div>}
    </div>
  );
}

function SkeletonCard({ tall }) {
  return (
    <div className={`ae-card p-3 animate-pulse ${tall ? "h-32" : "h-20"}`}>
      <div className="h-2 w-1/3 bg-slate-700/60 rounded" />
      <div className="h-5 w-2/3 bg-slate-700/40 rounded mt-2" />
      {tall && <div className="h-2 w-1/2 bg-slate-700/40 rounded mt-3" />}
    </div>
  );
}

function CompsPanel({ title, count, lines, lastPulled, radius, hasRaw, onView, testid }) {
  return (
    <div className="ae-card p-4 space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{title}</div>
        <div className="text-[11px] text-emerald-400">{typeof count === "number" ? `${count} within ${radius ?? 0.5} mi` : "No data"}</div>
      </div>
      <ul className="text-xs text-slate-200 space-y-0.5 font-mono">
        {lines.map((ln) => <li key={ln}>{ln}</li>)}
      </ul>
      <div className="flex items-center justify-between pt-1">
        <div className="text-[10px] text-slate-500">{lastPulled ? `Pulled ${lastPulled}` : "Not yet pulled"}</div>
        <button
          type="button"
          onClick={onView}
          disabled={!hasRaw}
          data-testid={`${testid}-view`}
          className="text-[10px] inline-flex items-center gap-1 text-slate-300 hover:text-emerald-300 disabled:opacity-40"
        >
          <Eye className="h-3 w-3" /> View Full {title}
        </button>
      </div>
    </div>
  );
}

function OwnerStrip({ opp }) {
  const absentee = opp.rc_absentee_confirmed === true;
  const ownerOcc = opp.rc_absentee_confirmed === false;
  return (
    <div className="ae-card p-3" data-testid="rmi-owner-strip">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <Home className="h-3 w-3 text-slate-400" />
        <span className="text-slate-300">
          <span className="text-slate-500">Owner:</span> <span className="font-medium">{opp.rc_owner_name || "Unknown"}</span>
          <span className="text-slate-700 mx-2">|</span>
          <span className="text-slate-500">Type:</span> <span className="font-medium">{opp.rc_owner_type || "Unknown"}</span>
          <span className="text-slate-700 mx-2">|</span>
          <span className="text-slate-500">Mailing:</span> <span className="font-mono text-[11px]">{opp.rc_mailing_address || "Unknown"}</span>
        </span>
        {absentee && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-300 border border-amber-500/30" data-testid="rmi-absentee-badge">
            Absentee Owner Confirmed
          </span>
        )}
        {ownerOcc && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30" data-testid="rmi-owner-occupied-badge">
            Owner Occupied
          </span>
        )}
      </div>
    </div>
  );
}

function CompsModal({ title, items, kind, onClose }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-6 bg-black/70" data-testid="comps-modal">
      <div className="bg-[#05070A] border border-white/10 rounded-sm shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between px-5 py-3 border-b border-white/8 bg-[#05070A]">
          <div className="text-sm font-semibold tracking-wide text-slate-100">{title}</div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100" data-testid="comps-modal-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-slate-500 border-b border-white/8">
              <th className="text-left px-3 py-2.5">Address</th>
              {kind === "rental" ? (
                <>
                  <th className="text-right px-3 py-2.5">Rent</th>
                  <th className="text-right px-3 py-2.5">SqFt</th>
                  <th className="text-right px-3 py-2.5">Bed/Bath</th>
                </>
              ) : (
                <>
                  <th className="text-right px-3 py-2.5">Price</th>
                  <th className="text-right px-3 py-2.5">SqFt</th>
                  <th className="text-right px-3 py-2.5">Year</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(items || []).map((c, i) => (
              <tr key={i} className="border-b border-white/5">
                <td className="px-3 py-2 text-slate-200">
                  <MapPinned className="h-3 w-3 inline mr-1 text-slate-500" />
                  {c.address || "Unknown"}
                </td>
                {kind === "rental" ? (
                  <>
                    <td className="px-3 py-2 text-right font-mono">{c.rent ? `${fmtMoney(c.rent)}/mo` : "N/A"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.sqft || "N/A"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.bedrooms ?? "N/A"}/{c.bathrooms ?? "N/A"}</td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right font-mono">{c.price ? fmtCurrencyFull(c.price) : "N/A"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.sqft || "N/A"}</td>
                    <td className="px-3 py-2 text-right font-mono">{c.year_built || "N/A"}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {(!items || items.length === 0) && (
          <div className="px-5 py-8 text-xs text-slate-500 text-center">No comp records returned by RentCast.</div>
        )}
      </div>
    </div>
  );
}
